


















































































































































































// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios';
import * as Sentry from '@sentry/vue';
// eslint-disable-next-line import/no-extraneous-dependencies
import { isEmail } from 'validator';
import OA2Providers from '@/components/auth_methods/OA2Providers.vue';
import PasswordStep from '@/components/Templates/login/PasswordStep.vue';
import PasswordlessStep from '@/components/Templates/login/PasswordlessStep.vue';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoModal from '@/components/Organisms/co-modal/CoModal.vue';
import CoHeadline from '@/components/Atoms/co-headline/CoHeadline.vue';
import CoText from '@/components/Atoms/co-text/CoText.vue';
import CoCard from '@/components/Molecules/co-card/CoCard.vue';
import CoFormGroup from '@/components/Molecules/co-form-group/CoFormGroup.vue';
import CoInput from '@/components/Molecules/co-input/CoInput.vue';
import CoAlert from '@/components/Molecules/co-alert/CoAlert.vue';
import CoLink from '@/components/Atoms/co-link/CoLink.vue';
import CoRoundButton from '@/components/Atoms/co-round-button/CoRoundButton.vue';
import LoginAccountNotFoundStep from '@/components/Templates/login/LoginAccountNotFoundStep.vue';

const normalizeRedirectTarget = (rawRedirect) => {
    const redirect = Array.isArray(rawRedirect) ? rawRedirect[0] : rawRedirect;

    if (!redirect || typeof redirect !== 'string') {
        return null;
    }

    const isInternalRedirect = (target) => target.startsWith('/') && !target.startsWith('//');

    try {
        const normalizedRedirect = decodeURIComponent(redirect);
        return isInternalRedirect(normalizedRedirect) ? normalizedRedirect : null;
    } catch (error) {
        return isInternalRedirect(redirect) ? redirect : null;
    }
};

const bffRequestConfig = {
    withCredentials: true,
    xsrfCookieName: '_csrf',
    xsrfHeaderName: 'X-XSRF-TOKEN',
};

// eslint-disable-next-line vue/multi-word-component-names
export default {
    name: 'PublicLogin',
    components: {
        OA2Providers,
        PasswordStep,
        PasswordlessStep,
        CoButton,
        CoModal,
        CoHeadline,
        CoText,
        CoCard,
        CoFormGroup,
        CoInput,
        CoAlert,
        CoLink,
        CoRoundButton,
        LoginAccountNotFoundStep,
    },
    data() {
        return {
            email: '',
            invalidInviteLink: this.$route.query.invalidInviteLink,
            // Keep the initial login redirect across the whole auth flow, even if the route query is replaced later.
            redirectTarget: normalizeRedirectTarget(this.$route.query.redirect),
            errorContentVisible: true,
            loginState: {
                errorMessage: null,
                loading: false,
                flow: {
                    availableMethods: [],
                    email: '',
                    passwordlessStarted: false,
                    step: 'email',
                },
            },
        };
    },
    computed: {
        authErrorMessage() {
            return this.loginState.errorMessage;
        },
        canUsePassword() {
            return this.loginState.flow.availableMethods.includes('password');
        },
        canUsePasswordless() {
            return this.loginState.flow.availableMethods.includes('passwordless');
        },
        currentStep() {
            return this.loginState.flow.step;
        },
        loading() {
            return this.loginState.loading;
        },
        normalizedEmail() {
            return (this.loginState.flow.email || this.email || '').trim().toLowerCase();
        },
        passwordlessStarted() {
            return this.loginState.flow.passwordlessStarted;
        },
        SupportURL() {
            if (typeof this.$store?.state?.space?.SupportURL === 'string') {
                let url = this.$store.state.space.SupportURL.trim();
                if (isEmail(url)) {
                    url = `mailto:${url}`;
                } else {
                    url = url.startsWith('http') ? url : `https://${url}`;
                }
                return url.trim();
            }
            return null;
        },
        hasValidEmail() {
            return this.validateEmail(this.email);
        },
        registrationsAreClosed() {
            return Boolean(this.$route.query.registrationsAreClosed);
        },
    },
    watch: {
        '$route.query.redirect': function updateRedirectTarget(value) {
            const redirectTarget = normalizeRedirectTarget(value);
            if (redirectTarget) {
                this.redirectTarget = redirectTarget;
            }
        },
    },
    async mounted() {
        this.$store.commit('RESET_MEMBERSHIP_PROVIDER');

        if (this.$store.getters.isAuthenticated) {
            this.redirectAfterLogin();
            return;
        }

        if (this.invalidInviteLink) {
            this.$refs.invalidInviteLinkMessageModal.show();
        }
    },
    methods: {
        routerPush(route) {
            this.$router.push(route).catch(() => undefined);
        },
        removeInvalidInviteLink() {
            this.invalidInviteLink = null;
            this.$router.replace({ query: {} });
            this.$refs.invalidInviteLinkMessageModal.hide();
        },
        retryInvalidInviteLink() {
            this.$refs.invalidInviteLinkMessageModal.hide();
            this.$router.go(this.invalidInviteLink);
        },

        async continueWithEmail() {
            let { flow } = this.loginState;
            this.loginState.loading = true;

            if (!this.validateEmail(this.email)) {
                this.loginState.errorMessage = this.$t('errors.mustBeValidEmail');
                this.$refs.email.focus();
                this.loginState.loading = false;
                return;
            }

            const normalizedEmail = this.email.trim().toLowerCase();

            this.loginState.errorMessage = null;

            // Bootstrap the next login step directly from the BFF so this view owns the email step flow.
            await axios
                .post(
                    `${window.location.origin}/bff/login/options`,
                    {
                        email: normalizedEmail,
                    },
                    bffRequestConfig
                )
                .then(async (response) => {
                    if (this.loginState.flow !== flow) return;
                    const availableMethods = [];

                    if (response?.data?.password_allowed) {
                        availableMethods.push('password');
                    }

                    if (response?.data?.passwordless_allowed) {
                        availableMethods.push('passwordless');
                    }

                    const nextStep = response?.data?.password_allowed ? 'password' : 'passwordless';

                    this.loginState.flow = {
                        availableMethods,
                        email: normalizedEmail,
                        expiresIn: response?.data?.expires_in || 0,
                        passwordlessExpiresIn: 0,
                        passwordlessStarted: false,
                        step: nextStep,
                    };
                    flow = this.loginState.flow;

                    if (response?.data?.passwordless_allowed && !response?.data?.password_allowed) {
                        // Passwordless-only accounts receive their one-time code as soon as their email is accepted.
                        await this.startPasswordless();
                        return;
                    }

                    this.loginState.loading = false;
                })
                .catch((error) => {
                    if (this.loginState.flow !== flow) return;
                    this.loginState.loading = false;
                    this.setAuthError(error);
                    this.$refs.email.focus();
                });
        },
        choosePassword() {
            // reset any previous error message when choosing password
            this.resetError();
            this.loginState.flow.step = 'password';
        },
        async choosePasswordless() {
            const { flow } = this.loginState;
            // reset any previous error message when choosing passwordless
            this.resetError();
            if (!this.passwordlessStarted) {
                await this.startPasswordless();
            }

            if (this.loginState.flow !== flow) return;
            this.loginState.flow.step = 'passwordless';
        },
        async submitPassword(password) {
            if (!this.validatePassword(password)) {
                this.loginState.errorMessage = this.$t('messages.auth.invalidInput');
                return;
            }

            const { flow } = this.loginState;
            try {
                this.loginState.loading = true;
                const response = await axios.post(
                    `${window.location.origin}/bff/login/password`,
                    { password },
                    bffRequestConfig
                );
                if (this.loginState.flow !== flow) return;
                if (!response.data?.authenticated) {
                    this.loginState.errorMessage = this.$t('messages.auth.additionalVerificationRequired');
                    return;
                }
                this.$store.commit('SET_AUTH_STATE', { authenticated: true, session: response.data });
            } catch (error) {
                if (this.loginState.flow !== flow) return;
                this.handleAuthError(error);
                return;
            } finally {
                if (this.loginState.flow === flow) this.loginState.loading = false;
            }
            this.redirectAfterLogin();
        },
        async startPasswordless() {
            const { flow } = this.loginState;
            try {
                this.loginState.loading = true;
                const response = await axios.post(
                    `${window.location.origin}/bff/login/passwordless/start`,
                    null,
                    bffRequestConfig
                );
                if (this.loginState.flow !== flow) return;
                this.loginState.flow.passwordlessStarted = true;
                this.loginState.flow.passwordlessExpiresIn = response.data?.expires_in || 0;
            } catch (error) {
                if (this.loginState.flow !== flow) return;
                this.handleAuthError(error);
            } finally {
                if (this.loginState.flow === flow) this.loginState.loading = false;
            }
        },
        async verifyPasswordless(otp) {
            if (!otp || !otp.trim()) {
                this.loginState.errorMessage = this.$t('messages.auth.otpRequired');
                return;
            }

            const { flow } = this.loginState;
            try {
                this.loginState.loading = true;
                const response = await axios.post(
                    `${window.location.origin}/bff/login/passwordless/verify`,
                    { otp: otp.trim() },
                    bffRequestConfig
                );
                if (this.loginState.flow !== flow) return;
                if (!response.data?.authenticated) {
                    // Only the explicit registration status identifies a missing community account.
                    if (response.data?.status === 'registration_required') {
                        // The completed code verification supersedes any previous invalid-code error.
                        this.loginState.errorMessage = null;
                        this.loginState.flow.step = 'accountNotFound';
                    } else {
                        this.loginState.errorMessage = this.$t('messages.auth.additionalVerificationRequired');
                    }
                    return;
                }
                this.$store.commit('SET_AUTH_STATE', { authenticated: true, session: response.data });
                this.redirectAfterLogin();
            } catch (error) {
                if (this.loginState.flow !== flow) return;
                if (error?.response?.data?.message === 'invalid passwordless login') {
                    // Keep the active OTP form available so the user can correct and resubmit their code.
                    this.loginState.errorMessage = this.$t('messages.auth.invalidOtp');
                    return;
                }

                this.handleAuthError(error);
            } finally {
                if (this.loginState.flow === flow) this.loginState.loading = false;
            }
        },
        resetToEmail() {
            this.loginState.errorMessage = null;
            this.loginState.loading = false;
            // Replacing the flow invalidates pending requests and their loading-state cleanup.
            this.loginState.flow = {
                availableMethods: [],
                email: '',
                passwordlessStarted: false,
                step: 'email',
            };
        },
        handleAuthError(error) {
            if (this.handleExpiredSession(error)) {
                return;
            }
            this.setAuthError(error);
        },
        handleExpiredSession(error) {
            if (error?.response?.status === 401 && error.response?.data?.message === 'login session not found') {
                // login session has expired or is invalid
                Sentry.captureException(error);
                // reset the login state to the email step BEFORE showing the error message
                this.resetToEmail();
                this.loginState.errorMessage = this.$t('messages.auth.expiredOrInvalidSession');
                return true;
            }
            // allow fall through for other errors
            return false;
        },
        setAuthError(error) {
            Sentry.captureException(error);
            const status = error?.response?.status;
            const serverMessage = error?.response?.data?.message;

            if (status === 400 && serverMessage) {
                this.loginState.errorMessage = serverMessage;
            } else if (status === 401) {
                this.loginState.errorMessage = this.$t('messages.auth.invalidCredentials');
            } else if (status === 503) {
                this.loginState.errorMessage = this.$t('messages.auth.serviceUnavailable');
            } else {
                this.loginState.errorMessage = this.$t('messages.auth.unexpectedError');
            }
        },
        async redirectAfterLogin() {
            if (this.redirectTarget && this.redirectTarget !== '/') {
                this.routerPush(this.redirectTarget);
                return;
            }

            try {
                const { data: user } = await this.$store.dispatch('getMe');

                // Guests can only access their events, so make it their default post-login destination.
                if (user?.Status === 7) {
                    this.routerPush('/profile/my-events');
                    return;
                }
            } catch (error) {
                Sentry.captureException(error);
            }

            this.routerPush('/');
        },
        goToResetPassword() {
            // Preserve the typed email so the reset-password form can prefill it.
            const email = this.normalizedEmail;

            this.routerPush(email ? `/reset-password?email=${encodeURIComponent(email)}` : '/reset-password');
        },
        validateEmail(value) {
            return Boolean(value) && isEmail(value);
        },
        validatePassword(value) {
            if (!value) {
                return false;
            }

            return value.length >= 1 && value.length <= 56;
        },
        hideMaxUsersReachedError() {
            this.$router.push({
                path: '/login',
            });
        },
        resetError() {
            this.loginState.errorMessage = '';
        },
    },
};
